import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import InlineAlert from "../ui/InlineAlert";
import { GITHUB_WATCH_WINDOW_MS, createGithubRefreshWatch } from "./githubRefreshWatch";

function safeGithubUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "github.com" && url.pathname.startsWith("/apps/");
  } catch {
    return false;
  }
}

function safeManageUrl(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.hostname !== "github.com") return false;
    return /^\/settings\/installations\/\d+$/.test(url.pathname)
      || /^\/organizations\/[^/]+\/settings\/installations\/\d+$/.test(url.pathname);
  } catch {
    return false;
  }
}

function repoKey(installationId, repo) {
  return `${installationId}:${repo.id || repo.fullName}`;
}

export default function RepositoryConnectPanel({
  setup = null,
  repositories = [],
  canConnect = false,
  initiallyOpen = false,
  onConnected,
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const [publicUrl, setPublicUrl] = useState("");
  const [installations, setInstallations] = useState([]);
  const [available, setAvailable] = useState([]);
  const [repoQuery, setRepoQuery] = useState("");
  const [loadingPrivate, setLoadingPrivate] = useState(false);
  const [privateLoaded, setPrivateLoaded] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const awaitingGitHub = useRef(false);
  const knownKeys = useRef(new Set());
  const watchRef = useRef(null);
  const loadPrivateRef = useRef(null);

  useEffect(() => setOpen(initiallyOpen), [initiallyOpen]);

  const connected = setup?.installationState === "active";
  const connectedKey = repositories
    .map((repo) => `${String(repo.owner || "").toLowerCase()}/${String(repo.name || "").toLowerCase()}`)
    .sort()
    .join("|");

  loadPrivateRef.current = async () => {
    if (!open || !connected) return { done: false, added: [] };
    setLoadingPrivate(true);
    setError("");
    try {
      const response = await api.get("/github-app/installations");
      const rows = response.data?.installations || [];
      const pages = await Promise.all(rows.filter((row) => !row.suspendedAt).map(async (row) => {
        const repos = await api.get(`/github-app/installations/${row.installationId}/repos`, {
          params: { fresh: Date.now() },
        });
        return (repos.data?.repositories || []).map((repo) => ({ ...repo, githubInstallationId: row.installationId }));
      }));
      const connectedNames = new Set(connectedKey ? connectedKey.split("|") : []);
      const next = pages.flat()
        .filter((repo) => repo.owner && repo.name)
        .map((repo) => ({
          ...repo,
          alreadyConnected: connectedNames.has(`${String(repo.owner).toLowerCase()}/${String(repo.name).toLowerCase()}`),
        }));
      const added = next.filter((repo) => !repo.alreadyConnected && !knownKeys.current.has(repoKey(repo.githubInstallationId, repo)));
      knownKeys.current = new Set(next.map((repo) => repoKey(repo.githubInstallationId, repo)));
      setInstallations(rows);
      setAvailable(next);
      let done = false;
      if (awaitingGitHub.current && added.length) {
        awaitingGitHub.current = false;
        done = true;
        const label = added[0].fullName || `${added[0].owner}/${added[0].name}`;
        setNotice(added.length === 1 ? `${label} is ready to add.` : `${added.length} repositories are ready to add.`);
      }
      setPrivateLoaded(true);
      return { done, added };
    } catch {
      setError("Private repositories could not be loaded. You may need to reconnect GitHub.");
      return { done: false, added: [] };
    } finally {
      setLoadingPrivate(false);
    }
  };

  useEffect(() => {
    if (!open || !connected) return undefined;
    let cancelled = false;
    loadPrivateRef.current?.().finally(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [open, connected, connectedKey, reloadToken]);

  function refreshList() {
    setReloadToken((value) => value + 1);
  }

  function watchForNewRepositories() {
    awaitingGitHub.current = true;
    if (!watchRef.current) {
      watchRef.current = createGithubRefreshWatch({
        refresh: async () => loadPrivateRef.current?.() || { done: false },
        isActive: () => awaitingGitHub.current,
        onIdle: () => {
          awaitingGitHub.current = false;
        },
      });
    }
    watchRef.current.start(GITHUB_WATCH_WINDOW_MS);
  }

  useEffect(() => {
    function refreshAfterGitHub() {
      if (document.visibilityState === "hidden") return;
      if (!awaitingGitHub.current || !watchRef.current?.active) return;
      watchRef.current.kick();
    }
    window.addEventListener("focus", refreshAfterGitHub);
    document.addEventListener("visibilitychange", refreshAfterGitHub);
    return () => {
      watchRef.current?.stop("unmount");
      watchRef.current = null;
      window.removeEventListener("focus", refreshAfterGitHub);
      document.removeEventListener("visibilitychange", refreshAfterGitHub);
    };
  }, []);

  async function addPublic(event) {
    event.preventDefault();
    if (!publicUrl.trim()) return;
    setPending("public");
    setError("");
    setNotice("");
    try {
      const response = await api.post("/workspace/repositories", { repositoryUrl: publicUrl.trim() });
      setNotice(`${response.data.repository.owner}/${response.data.repository.name} is ready. You can review a pull request now.`);
      setPublicUrl("");
      onConnected?.(response.data.repository);
    } catch (err) {
      setError(err?.response?.data?.error || "That repository could not be added.");
    } finally {
      setPending("");
    }
  }

  async function connectGitHub() {
    const tab = window.open("", "_blank");
    setPending("github");
    setError("");
    try {
      const response = await api.get("/github-app/install-url");
      const url = response.data?.installUrl;
      if (!safeGithubUrl(url)) throw new Error("unsafe");
      if (!tab) {
        setError("GitHub was blocked by the browser. Allow pop-ups for this site, then try again.");
        setPending("");
        return;
      }
      watchForNewRepositories();
      tab.location.href = url;
    } catch {
      tab?.close?.();
      setError("GitHub connection is not available right now.");
      setPending("");
    }
  }

  async function addPrivate(repo) {
    const key = repoKey(repo.githubInstallationId, repo);
    setPending(key);
    setError("");
    setNotice("");
    try {
      const response = await api.post("/workspace/repositories", {
        githubInstallationId: repo.githubInstallationId,
        owner: repo.owner,
        name: repo.name,
      });
      setAvailable((items) => items.filter((item) => repoKey(item.githubInstallationId, item) !== key));
      setNotice(`${repo.fullName || `${repo.owner}/${repo.name}`} is ready. You can review a pull request now.`);
      onConnected?.(response.data.repository);
    } catch (err) {
      setError(err?.response?.data?.error || "That repository could not be added.");
    } finally {
      setPending("");
    }
  }

  const manageable = installations.filter((row) => !row.suspendedAt && safeManageUrl(row.manageUrl));
  const query = repoQuery.trim().toLowerCase();
  const visible = [...available]
    .sort((a, b) => Number(a.alreadyConnected) - Number(b.alreadyConnected) || String(a.fullName).localeCompare(String(b.fullName)))
    .filter((repo) => {
      const label = `${repo.fullName || ""} ${repo.owner}/${repo.name}`.toLowerCase();
      return !query || label.includes(query);
    });

  return (
    <section className={`rr-connect ${open ? "is-open" : ""}`} aria-label="Repository access">
      <div className="rr-connect-head">
        <div>
          <span className="rr-eyebrow">Codebases</span>
          <h2>{repositories.length ? `${repositories.length} ${repositories.length === 1 ? "codebase" : "codebases"} connected` : "Connect a codebase"}</h2>
          <p>Add a public codebase, or connect GitHub for private code and pull-request checks.</p>
        </div>
        <button type="button" className="rr-button secondary" onClick={() => setOpen((value) => !value)}>
          {open ? "Done" : repositories.length ? "Add codebase" : "Choose a source"}
        </button>
      </div>
      {open ? (
        <div className="rr-connect-options">
          <form className="rr-connect-option" onSubmit={addPublic}>
            <span className="rr-source-icon" aria-hidden="true">↗</span>
            <div>
              <h3>Public repository</h3>
              <p>Paste a GitHub repository URL. No installation or indexing wait.</p>
              <div className="rr-inline-input">
                <input aria-label="Public repository URL" value={publicUrl} onChange={(event) => setPublicUrl(event.target.value)} placeholder="github.com/company/product" />
                <button className="rr-button" type="submit" disabled={!publicUrl.trim() || pending === "public"}>{pending === "public" ? "Adding…" : "Add"}</button>
              </div>
            </div>
          </form>
          <div className="rr-connect-option">
            <span className="rr-source-icon private" aria-hidden="true">⌁</span>
            <div>
              <h3>Private GitHub</h3>
              <p>Select a repository on GitHub that RenderRepo cannot see yet, then add it here.</p>
              {!connected ? (
                <button type="button" className="rr-button secondary" onClick={connectGitHub} disabled={!canConnect || pending === "github"}>
                  {pending === "github" ? "Opening GitHub…" : "Connect GitHub"}
                </button>
              ) : !privateLoaded ? (
                <button type="button" className="rr-button secondary" disabled>Loading repositories…</button>
              ) : manageable.length ? (
                <div className="rr-private-actions">
                  {manageable.map((row) => (
                    <a
                      key={row.installationId}
                      className="rr-button"
                      href={row.manageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={watchForNewRepositories}
                    >
                      {manageable.length > 1 ? `Select a repository for ${row.accountLogin}` : "Select a new repository"}
                    </a>
                  ))}
                  <p className="rr-private-note">
                    GitHub opens in a new tab. Save there, and this list updates here with a short bounded refresh.{" "}
                    <button type="button" className="rr-text-button" onClick={refreshList} disabled={loadingPrivate}>
                      {loadingPrivate ? "Refreshing…" : "Refresh list"}
                    </button>
                  </p>
                </div>
              ) : (
                <button type="button" className="rr-button" onClick={connectGitHub} disabled={!canConnect || pending === "github"}>
                  {pending === "github" ? "Opening GitHub…" : "Select a new repository"}
                </button>
              )}
            </div>
          </div>
          {visible.length ? (
            <div className="rr-private-list">
              <p className="rr-eyebrow">Ready to add</p>
              {available.length > 6 ? (
                <input
                  aria-label="Find a repository"
                  value={repoQuery}
                  onChange={(event) => setRepoQuery(event.target.value)}
                  placeholder="Find a repository"
                />
              ) : null}
              {visible.map((repo) => (
                <button key={repoKey(repo.githubInstallationId, repo)} type="button" onClick={() => addPrivate(repo)} disabled={Boolean(pending) || repo.alreadyConnected}>
                  <span><strong>{repo.fullName}</strong><small>{repo.private ? "Private" : "Public"}{repo.alreadyConnected ? " · already in this workspace" : ""}</small></span>
                  <b>{repo.alreadyConnected ? "Connected" : pending === repoKey(repo.githubInstallationId, repo) ? "Adding…" : "Add"}</b>
                </button>
              ))}
            </div>
          ) : privateLoaded && connected && !loadingPrivate && !available.length ? (
            <p className="rr-private-empty">Every repository GitHub has shared is already connected. Select a new repository to grant another.</p>
          ) : null}
        </div>
      ) : null}
      {notice ? <InlineAlert>{notice}</InlineAlert> : null}
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
    </section>
  );
}

